import React from 'react';
import PropTypes from 'prop-types';

// @material-ui/core
import { makeStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import IconButton from '@material-ui/core/IconButton';
import Icon from '@material-ui/core/Icon';

import palette from '../../../../../design-system/theme/palette';

// Estados posibles: 'assigned' | 'rejected' | 'error'

const useStyles = makeStyles((theme) => ({
    paper: {
        maxWidth: 480,
        backgroundColor: palette.colors.fillStroke[400],
        border: `1px solid ${palette.colors.fillStroke[200]}`,
        borderRadius: 16,
    },
    closeButton: {
        position: 'absolute',
        right: 8,
        top: 8,
        color: palette.colors.gray[100],
    },
    image: {
        width: 200,
        height: 'auto',
        marginBottom: theme.spacing(2),
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        padding: theme.spacing(4, 3, 2),
    },
    actions: {
        display: 'flex',
        justifyContent: 'center',
        padding: theme.spacing(1, 3, 3),
    },
    successIcon: {
        fontSize: 64,
        color: palette.colors.success[300],
        marginBottom: theme.spacing(2),
    },
    errorIcon: {
        fontSize: 64,
        color: palette.colors.error[300],
        marginBottom: theme.spacing(2),
    },
    waitTime: {
        fontWeight: 700,
        color: palette.colors.orange[300],
    },
    spinner: {
        marginBottom: theme.spacing(2),
    },
}));

const statusConfig = {
    loading: {
        title: 'Enviando solicitud',
        message: 'Se está enviando la solicitud para asignar tu cuenta.',
        useImage: false,
        isLoading: true,
    },
    processing: {
        icon: 'ri-time-line',
        iconStyle: 'successIcon',
        title: 'Solicitud en proceso',
        message: 'Se está procesando tu solicitud. El acceso estará listo en un lapso de 2 a 24 horas. Te puede llegar un correo de ChatGPT u OpenAI indicándote que el acceso ya está disponible.',
        highlight: 'El acceso estará listo en un lapso de 2 a 24 horas',
        useImage: false,
    },
    assigned: {
        icon: 'ri-checkbox-circle-fill',
        iconStyle: 'successIcon',
        title: '¡Licencia asignada!',
        message: 'Tu licencia de Chat TALI ha sido asignada exitosamente. El acceso se verá reflejado en un lapso de 2 a 24 horas.',
        useImage: false,
    },
    unassigned: {
        title: 'Acceso no disponible',
        message: 'Actualmente no cuentas con una licencia activa para Chat TALI. Contacta a tu administrador para solicitar acceso.',
        useImage: true,
    },
    error: {
        icon: 'ri-close-circle-fill',
        iconStyle: 'errorIcon',
        title: 'No se pudo asignar la licencia',
        message: 'Ocurrió un problema al intentar asignar tu licencia de Chat TALI. Por favor, inténtalo de nuevo más tarde o contacta a tu administrador.',
        useImage: false,
    },
};

const renderMessage = (config, highlightClass) => {
    if (!config.highlight || !config.message.includes(config.highlight)) {
        return config.message;
    }

    const [before, after] = config.message.split(config.highlight);

    return (
        <>
            {before}
            <span className={highlightClass}>{config.highlight}</span>
            {after}
        </>
    );
};

const LicenseAssignedConfirmation = ({ open, onClose, status }) => {
    const classes = useStyles();
    const config = statusConfig[status] || statusConfig.unassigned;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            disableBackdropClick
            disableEscapeKeyDown
            fullWidth
            maxWidth="xs"
            classes={{ paperWidthXs: classes.paper }}
        >
            {!config.isLoading && (
                <IconButton className={classes.closeButton} onClick={onClose} aria-label="cerrar">
                    <Icon className="ri-close-line" />
                </IconButton>
            )}

            <DialogContent className={classes.content}>
                {config.isLoading ? (
                    <CircularProgress color="primary" className={classes.spinner} />
                ) : config.useImage ? (
                    <img
                        src="/images/theme/maintenance.svg"
                        alt="Sin acceso"
                        className={classes.image}
                    />
                ) : (
                    <Icon className={`${config.icon} ${classes[config.iconStyle]}`} />
                )}
                <Typography variant="h4" gutterBottom>
                    {config.title}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    {renderMessage(config, classes.waitTime)}
                </Typography>
            </DialogContent>

            {!config.isLoading && (
                <DialogActions className={classes.actions}>
                    <Button variant="contained" color="primary" onClick={onClose}>
                        Entendido
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
};

LicenseAssignedConfirmation.defaultProps = {
    open: false,
    status: 'unassigned',
};

LicenseAssignedConfirmation.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    status: PropTypes.oneOf(['loading', 'processing', 'assigned', 'unassigned', 'error']),
};

export default LicenseAssignedConfirmation;
